import { useState, useCallback, useEffect, useMemo, Fragment, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ColorPicker from '../../../../../components/ColorPicker';
import Button from '../../../../../components/Button';
import Label from '../../../../../components/Label';
import Tabs from '../../../../../components/Tabs';
import Tab from '../../../../../components/Tab';
import ReactSlider from 'rc-slider';
import CheckboxWrapper from '../../../../../../../DataTable/Parts/CheckboxWrapper';
import {
  postBrokerProductStyle,
  putBrokerProductStyle,
  getBrokerProductSummary,
} from '../../../../../../../../services/integrationResources';
import { selectUserInfo } from '../../../../../../../../store/selectors';
import { selectBrokerProduct } from '../../../../../../../../store/selectors/brokerProducts';
import { setGlobalStyles, DEFAULT_STYLES, sliderSetup, sliderSetupButton } from './helpers';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import Notification from '../../../../../../../../components/Utils/Notification';

const Options = ({ onRequestClose, selectedTab }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { recommendationCode } = useSelector(selectUserInfo)?.data || {};
  const brokerProductStyles = useSelector(selectBrokerProduct)?.data?.data?.styles;
  const brokerProduct = useSelector(selectBrokerProduct)?.data?.data?.name || {};
  const { search } = useLocation();

  const useQuery = () => new URLSearchParams(search);
  const query = useQuery();
  const product = query.get('product');

  const getSelectedProductDetails = useCallback(async () => {
    try {
      const result = await getBrokerProductSummary(recommendationCode, product);
      const mergedStyles = mergeStyles(result.data.styles, DEFAULT_STYLES);

      setStyles(mergedStyles);
      setGlobalStyles(result.data.styles, activeTab);

      dispatch({
        type: 'BROKER_PRODUCT_STYLES',
        payload: result.data.styles,
      });
    } catch (error) {
      console.log(error);
    }
  }, [recommendationCode, product]);

  const [styles, setStyles] = useState([]);
  const [saveToBoth, setSaveToBoth] = useState(false);
  const [activeTab, setActiveTab] = useState('form');
  const [windowBorderRadius, setWindowBorderRadius] = useState('6');
  const [buttonBorderRadius, setButtonBorderRadius] = useState('6');
  const [primaryColor, setPrimaryColor] = useState('#009DD7');
  const [secondaryColor, setSecondaryColor] = useState('#009DD7');

  const activeType = useMemo(() => (activeTab === 'form' ? 'form' : 'calculator'), [activeTab]);
  const oppositeType = useMemo(() => (activeType === 'form' ? 'calculator' : 'form'), [activeType]);
  const checkboxLabel = useMemo(() => `Save style to ${oppositeType}`, [oppositeType]);

  const updateStyles = (key, value) =>
    setStyles((prev) => prev.map((item) => (item.type === activeTab ? { ...item, styles: { ...item.styles, [key]: value } } : item)));

  const handleSetColorPrimary = useCallback(
    (color) => {
      updateStyles('primaryColor', color);
    },
    [updateStyles]
  );

  const handleSetColorSecondary = useCallback(
    (color) => {
      updateStyles('secondaryColor', color);
    },
    [updateStyles]
  );

  const handleWindowRadius = useCallback(
    (value) => {
      updateStyles('borderRadiusBody', value.toString());
    },
    [updateStyles]
  );

  const handleButtonRadius = useCallback(
    (value) => {
      updateStyles('borderRadiusButton', value.toString());
    },
    [updateStyles]
  );

  const handleSaveStyleToBoth = useCallback(() => {
    setSaveToBoth((prev) => !prev);
  }, []);

  const saveStyle = useCallback(
    (type, style, applyToAllStyles) => {
      const existingStyle = brokerProductStyles.find((s) => s.type === type);

      const mergedData = {
        ...style,
        styles: {
          ...style.styles,
          logoFileId: existingStyle ? existingStyle.styles.logoFileId : null,
        },
      };

      const data = { ...mergedData, product: brokerProduct, applyToAllStyles: applyToAllStyles };

      const savePromise = existingStyle?.styleId
        ? putBrokerProductStyle(recommendationCode, existingStyle.styleId, data)
        : postBrokerProductStyle(recommendationCode, data);

      savePromise
        .then(() => getSelectedProductDetails())
        .then(() =>
          Notification({
            type: 'success',
            text: formatMessage({ id: 'Style has been saved' }),
          }).show()
        )
        .catch(() =>
          Notification({
            type: 'error',
            text: formatMessage({
              id: 'Something went wrong, try again later',
            }),
          }).show()
        );
    },
    [brokerProductStyles, brokerProduct, recommendationCode, getSelectedProductDetails]
  );

  const handleSave = useCallback(() => {
    const currentStyle = styles?.find((style) => style.type === activeType);

    if (currentStyle) {
      saveStyle(activeType, currentStyle, saveToBoth);
    }
  }, [styles, activeType, oppositeType, saveToBoth, saveStyle]);

  const handleActiveTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      selectedTab(tab);
      setStyles(styles);
    },
    [selectedTab, styles, activeTab]
  );

  const mergeStyles = (brokerStyles, defaultStyles) => {
    if (brokerStyles.length === 0) {
      return defaultStyles;
    } else {
      return brokerStyles.map((style) => {
        const defaultStyle = defaultStyles.find((ds) => ds.type === style.type);

        return {
          ...style,
          styles: {
            ...style.styles,
            ...Object.keys(style.styles).reduce((acc, key) => {
              acc[key] = style.styles[key] === null ? defaultStyle.styles[key] : style.styles[key];
              return acc;
            }, {}),
          },
        };
      });
    }
  };

  useEffect(() => {
    const styl = brokerProductStyles.length > 0 ? brokerProductStyles : styles;
    const mergedStyles = mergeStyles(styl, DEFAULT_STYLES);

    setStyles(mergedStyles);

    setGlobalStyles(styles, activeType);
  }, []);

  useEffect(() => {
    if (styles.length) {
      const existingStyle = styles.find((s) => s.type === activeType)?.styles;

      setWindowBorderRadius(existingStyle?.borderRadiusBody);
      setButtonBorderRadius(existingStyle?.borderRadiusButton);
      setPrimaryColor(existingStyle?.primaryColor);
      setSecondaryColor(existingStyle?.secondaryColor);

      setGlobalStyles(existingStyle, activeType);
    }
  }, [styles, activeTab]);

  useLayoutEffect(() => {
    selectedTab('form');
  }, []);

  return (
    <Fragment>
      <Tabs>
        <Tab label='Form' onCallback={handleActiveTab} dataValue='form' isActive={activeTab === 'form'} />
        <Tab label='Calculator' onCallback={handleActiveTab} dataValue='calculator' isActive={activeTab === 'calculator'} />
      </Tabs>

      <ColorPicker label='Styling main color' setColor={handleSetColorPrimary} color={primaryColor} />
      <ColorPicker label='Styling secondary color' setColor={handleSetColorSecondary} color={secondaryColor} />

      <Label message='Styling window border radius' />
      <div style={{ width: '100%', height: '50px' }}>
        <ReactSlider {...sliderSetup} value={windowBorderRadius} onChange={handleWindowRadius} />
      </div>

      <Label message='Styling button border radius' />
      <div style={{ width: '100%', height: '50px' }}>
        <ReactSlider {...sliderSetupButton} value={buttonBorderRadius} onChange={handleButtonRadius} />
      </div>

      <CheckboxWrapper disabled={false} selected={saveToBoth} handleClick={handleSaveStyleToBoth}>
        <Label message={checkboxLabel} />
      </CheckboxWrapper>

      <div className='formCalculatorStyling--options buttons-wrapper'>
        <Button label='Save styles' onCallback={handleSave} />
        <Button type='secondary' label='Cancel changes' onCallback={useCallback(() => onRequestClose(), [onRequestClose])} />
      </div>
    </Fragment>
  );
};

export default Options;
